/**
 * useCopyScanner — une page de la copie, du geste de l'élève à la file d'envoi (`copyOutbox`) : au
 * téléphone le scanner GeniusScan (`useMediaPickerAdapter`, l'exception 3 des ports), dans un
 * navigateur la caméra du design system (`StudentCopyCamera`).
 *
 * Une photo ne doit plus se perdre en route : le 24/10, sur iPhone, une page n'a jamais quitté le
 * téléphone, et rien ne l'a dit. Trois règles :
 *  - **l'activité visée est figée au geste** : une page prise pour une activité part vers elle, même
 *    si une autre s'est ouverte entre-temps (le tuteur en a poussé une, une activité faite est
 *    affichée). Avant, elle partait vers l'activité ouverte au retour du scanner, ou nulle part. La
 *    photo de la copie a son `formId` (`ACTIVITY_COPY`) : celle d'un énoncé (« Ajouter un énoncé »,
 *    `NEW_MATERIAL`) reste à son écran, au lieu de devenir une page ;
 *  - **la photo ne quitte le magasin qu'une fois dans la file**. Avant, elle était vidée même quand
 *    elle n'avait nulle part où aller ;
 *  - **chaque étape laisse sa trace** : le scanner ouvert, revenu avec ses pages, la page en file,
 *    et une erreur à tout échec (le scanner qui refuse ou ne répond plus, un retour sans page, une
 *    page sans prise en cours, un geste sans activité). Fermer le scanner sans photo n'est pas un
 *    échec : un fil d'Ariane suffit.
 *
 * Le scanner qui refuse ou ne répond plus passe la main à l'appareil photo de l'app (`useCamera`) :
 * sa page arrive par le même magasin, vers la même prise en cours.
 */
import { useEffect, useMemo, useRef } from "react"

import { formIds } from "../../../../../../data/constants/formConstants"
import { type CopyFile, type CopyTarget } from "../../../../../../engine/activity/copy/copyOutbox"
import { type ActivityRun } from "../../../../../../engine/activity/useActivityRun"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { useMediaPickerAdapter, type CameraScanEvents } from "../../../hooks/useMediaPickerAdapter"
import { type CopyPhotoOrigin } from "./StudentCopyCamera"

const telemetry = createTelemetry("activity.copy_scan", { role: "student" })

/** `import` : une image choisie dans les fichiers, faute de caméra au navigateur. */
type ScanSource = "app" | "browser" | "import"

/**
 * La prise en cours : l'activité visée, figée au geste. Une pour l'app, hors de l'écran : un écran
 * remonté pendant que le scanner est ouvert retrouve sa prise.
 */
let pending: CopyTarget | null = null

/** Les tests repartent d'aucune prise en cours. */
export function resetCopyScan() {
    pending = null
}

/** Le scanner renvoie un JPEG ; la caméra du navigateur, une adresse de données JPEG. */
const photoFile = (uri: string): CopyFile => ({ uri, mimeType: "image/jpeg", extension: ".jpg" })

const codeOf = (error: unknown) => {
    const code = (error as { code?: unknown } | null)?.code
    return typeof code === "string" ? code : null
}

const opened = (source: string) =>
    telemetry.transition("idle", "scanning", "opened", { source, activityId: pending?.activityId ?? null })

const scanEvents: CameraScanEvents = {
    opened: ({ source }) => opened(source),
    returned: ({ pages }) => {
        if (pages > 0) {
            telemetry.transition("scanning", "returned", "scanned", { pages })
            return
        }
        pending = null
        telemetry.error("empty_scan", new Error("the scanner returned without a page"))
    },
    cancelled: () => {
        pending = null
        telemetry.transition("scanning", "idle", "cancelled")
    },
    // L'appareil photo de l'app prend le relais : la prise en cours l'attend.
    failed: (error) => {
        telemetry.error("scan", error, { code: codeOf(error) })
    },
    unreturned: (info) => {
        telemetry.error("scan_unreturned", new Error("the previous scan never returned its result"), info)
    },
}

/** Le geste : l'activité ouverte devient la prise en cours. `false` : aucune activité, rien ne s'ouvre. */
function begin(run: Pick<ActivityRun, "copyTarget">, source: ScanSource): boolean {
    pending = run.copyTarget()
    if (pending == null) {
        telemetry.error("unrouted", new Error("no activity to send the copy to"), { source })
        return false
    }
    return true
}

/** Les pages vers la prise en cours ; `false` : rien n'est parti, et une erreur le dit. */
function enqueue(run: Pick<ActivityRun, "copyTarget" | "capture">, uris: string[], source: ScanSource): boolean {
    const target = pending
    if (target == null) {
        telemetry.error("unrouted", new Error("a copy page arrived without a capture under way"), {
            pages: uris.length,
            source,
        })
        return false
    }
    if (uris.map((uri) => run.capture(photoFile(uri), target)).some((id) => id == null)) {
        telemetry.error("not_queued", new Error("the outbox did not take the copy"), { pages: uris.length, source })
        return false
    }
    telemetry.transition("returned", "queued", "enqueued", {
        pages: uris.length,
        source,
        activityId: target.activityId,
        // L'activité ouverte a changé pendant la prise : la page part vers celle qu'on photographiait.
        elsewhere: target.activityId !== run.copyTarget()?.activityId,
    })
    return true
}

export function useCopyScanner(run: Pick<ActivityRun, "copyTarget" | "capture">) {
    const runRef = useRef(run)
    runRef.current = run

    const picker = useMediaPickerAdapter({ formId: formIds.ACTIVITY_COPY, multiple: false, scanEvents })
    const { incomingCameraImages, onCameraImagesConsumed } = picker
    const launchRef = useRef(picker.launchCamera)
    launchRef.current = picker.launchCamera

    // Le retour du scanner, par le magasin : vidé une fois les pages en file.
    useEffect(() => {
        if (incomingCameraImages.length === 0) return
        if (
            !enqueue(
                runRef.current,
                incomingCameraImages.map((image) => image.uri),
                "app"
            )
        )
            return
        pending = null
        onCameraImagesConsumed()
        // Le magasin seul réveille cet effet : le retour du scanner.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incomingCameraImages])

    // Les gestes ne lisent que des références : le même objet d'un rendu à l'autre du live.
    return useMemo(
        () => ({
            /** Le geste au téléphone : l'activité visée est figée, puis le scanner s'ouvre. */
            launchScanner: () => {
                if (begin(runRef.current, "app")) launchRef.current()
            },
            /** Le geste dans un navigateur : l'activité visée est figée ; `false`, la caméra ne s'ouvre pas. */
            openCamera: (): boolean => {
                if (!begin(runRef.current, "browser")) return false
                opened("browser")
                return true
            },
            /** Une photo prise par la caméra du navigateur, ou importée à sa place. */
            cameraPhoto: (uri: string, origin: CopyPhotoOrigin = "camera") => {
                enqueue(runRef.current, [uri], origin === "import" ? "import" : "browser")
            },
            cameraClosed: () => {
                pending = null
            },
        }),
        []
    )
}
